import React, { useCallback, useEffect, useState } from 'react';
import InfiniteScroll from 'react-infinite-scroll-component';
import { useHistory } from 'react-router-dom';
import AccessControl from '~/accessControl';
import { PERMISSION_GROUPS } from '~/accessControl/permissionGroups';
import Button from '~/app/common/Button';
import Icon from '~/app/common/Icon';
import { MaterialIcons as Icons } from '~/app/common/Icon/icons';
import Loader from '~/app/common/Loader';
import { route } from '~/app/helpers/route';
import usePageTitle from '~/app/hooks/usePageTitle';
import useTranslation from '~/app/hooks/useTranslation';
import { useAppHeader } from '~/app/modules/appHeader';
import { PROJECTS_ROUTES } from '~/projects/Router';
import { useStoreActions, useStoreState } from '~/store';
import { TASK_ROUTES } from '~/tasks/Router';
import { MachineOppgaver } from '~/types';
import List from './List';
import classes from './style.module.scss';
import { TIME_MANAGEMENT_ROUTES } from '~/timeManagement/Router';
import useAccessControl from '~/app/hooks/useAccessControl';
import { WORKORDER_ROUTES } from '~/workorder/Router';
import Search from './Search';
export type TasksComponent = React.FC;

const Tasks: TasksComponent = () => {

  const { t } = useTranslation('tasks');
  const { t: tb } = useTranslation('bottomNav');
  const hasTimeforingModule = useStoreState(state => state.tenant.hasTimeforingModule);
  const { checkPermission } = useAccessControl();
  const oppgaverPermission = checkPermission(PERMISSION_GROUPS.oppgaver);
  const timeManagementPermission = checkPermission(PERMISSION_GROUPS.timeManagement);
  const { setData, clearData } = useAppHeader();

  const userRole = useStoreState(state => state.user.currentRole);
  const networkStatus = useStoreState(state => state.app.offline.status);
  const fetching = useStoreState(state => state.tasks.fetching);
  const filters = useStoreState(state => state.tasks.filters);
  const history = useHistory();
  const TaskListAction = useStoreActions(actions => actions.tasks.fetch);

  const [machineList, setMachineList] = useState<MachineOppgaver[]>([]);
  const [currentPage, setCurrentPage] = useState<number>(1);
  const [hasMore, setHasMore] = useState(true);

  usePageTitle(t('pageTitle'));

  useEffect(() => {
    setData({
      title: t('pageTitle'),
      subTitle: '',
      backLink: undefined,
      isTaskPage: true
    });

  }, [setData, t]);

  useEffect(() => {
    return () => {
      clearData();
    };
  }, [clearData]);

  const fetchData = useCallback(async (page = currentPage, more = hasMore) => {
    if (fetching || !more || !userRole) return;

    const result = await TaskListAction({
      offset: (page - 1) * 50,
      filters,
      userRole
    });

    if (page === 1) {
      setMachineList(result || []);
    } else {
      setMachineList(prev => [...prev, ...(result || [])]);
    }

    if (!result || result.length < 50) {
      setHasMore(false);
    } else {
      setCurrentPage(prev => prev + 1);
    }
  }, [hasMore, currentPage, filters, TaskListAction, fetching, userRole]);

  const handleWorkorderClick = () => {
    history.push(route(WORKORDER_ROUTES.list));
  };

  useEffect(() => {
    setCurrentPage(1);
    setHasMore(true);

    fetchData(1, true);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filters, userRole]);

  return (
    <>
      {!networkStatus && (
        <>
        <div className={classes['header']}>
          <div className={`${classes['link']} ${classes['active']}`}>
              {tb('task')}
          </div>
          <div className={classes['link']} onClick={handleWorkorderClick}>
              {t('workorder')}
          </div>
        </div>
          <>
            <Search />
            <div className={classes['task-list']} id="tasks-scrollable-wrapper">
              <InfiniteScroll
                dataLength={machineList.length}
                next={fetchData}
                hasMore={hasMore}
                loader={fetching && <Loader />}
                scrollableTarget="tasks-scrollable-wrapper"
              >
                {fetching && currentPage === 1 ? (
                  <Loader />
                ) : machineList.length > 0 ? (
                  <span style={{ opacity: fetching ? 0.2 : 1 }}>
                    <List list={machineList} />
                  </span>
                ) : (
                  <p className={classes['noResult']}>{t('empty')}</p>
                )}
              </InfiniteScroll>
            </div>
          </>
        </>
      )}

      {(oppgaverPermission || (hasTimeforingModule && timeManagementPermission)) &&
        <div className={classes['bottomNav']}>
          <Button className={classes['bottomButton']} to={route(PROJECTS_ROUTES.list)}>
            <Icon name={'home' as Icons} className={classes['bottomIcon']} />
            {tb('home')}
          </Button>
          {hasTimeforingModule && (
            <AccessControl requiredPermissions={PERMISSION_GROUPS.timeManagement}>
              <Button
                className={classes['bottomButton']}
                to={route(TIME_MANAGEMENT_ROUTES.list)}
              >
                <Icon name={'schedule' as Icons} className={classes['bottomIcon']} />
                {tb('timeManagement')}
              </Button>
            </AccessControl>
          )}
          {oppgaverPermission && <Button className={classes['bottomButton']} to={route(TASK_ROUTES.list)}>
            <Icon name={'assignment' as Icons} className={classes['bottomIcon']} />
            {tb('task')}
          </Button>}
        </div>
      }
    </>
  );
};

export default Tasks;
